"use client";

import { useCallback, useRef, useState } from "react";
import type { RefObject } from "react";

export type TurnstileValidationState =
  | "idle"
  | "required"
  | "expired"
  | "error"
  | "verified";

interface UseTurnstileOptions {
  requiredMessage?: string;
  expiredMessage?: string;
  errorMessage?: string;
}

interface UseTurnstileReturn {
  isVisible: boolean;
  token: string | null;
  message: string | null;
  state: TurnstileValidationState;
  containerRef: RefObject<HTMLDivElement | null>;
  clear: () => void;
  handleError: (error?: unknown) => void;
  handleExpire: () => void;
  handleVerify: (token: string) => void;
  requestVerification: (message?: string) => void;
}

const DEFAULT_REQUIRED_MESSAGE =
  "Complete the quick security check below and the form will continue automatically.";
const DEFAULT_EXPIRED_MESSAGE =
  "The security check expired. Please try it again.";
const DEFAULT_ERROR_MESSAGE =
  "We could not verify the security check. Please try again.";

export function useTurnstile({
  requiredMessage = DEFAULT_REQUIRED_MESSAGE,
  expiredMessage = DEFAULT_EXPIRED_MESSAGE,
  errorMessage = DEFAULT_ERROR_MESSAGE,
}: UseTurnstileOptions = {}): UseTurnstileReturn {
  const [isVisible, setIsVisible] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [state, setState] = useState<TurnstileValidationState>("idle");
  const containerRef = useRef<HTMLDivElement>(null);

  const focusWidget = useCallback(() => {
    const node = containerRef.current;

    if (!node) {
      return;
    }

    if (typeof node.scrollIntoView === "function") {
      node.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    if (typeof node.focus === "function") {
      node.focus({ preventScroll: true });
    }
  }, []);

  const clear = useCallback(() => {
    setIsVisible(false);
    setToken(null);
    setMessage(null);
    setState("idle");
  }, []);

  const handleVerify = useCallback((nextToken: string) => {
    setIsVisible(false);
    setToken(nextToken);
    setMessage(null);
    setState("verified");
  }, []);

  const handleError = useCallback(
    (_error?: unknown) => {
      setIsVisible(true);
      setToken(null);
      setMessage(errorMessage);
      setState("error");
      focusWidget();
    },
    [errorMessage, focusWidget],
  );

  const handleExpire = useCallback(() => {
    setIsVisible(true);
    setToken(null);
    setMessage(expiredMessage);
    setState("expired");
  }, [expiredMessage]);

  const requestVerification = useCallback(
    (nextMessage?: string) => {
      setIsVisible(true);
      setToken(null);
      setMessage(nextMessage || requiredMessage);
      setState("required");
      focusWidget();
    },
    [focusWidget, requiredMessage],
  );

  return {
    isVisible,
    token,
    message,
    state,
    containerRef,
    clear,
    handleError,
    handleExpire,
    handleVerify,
    requestVerification,
  };
}
